import {
  DailyAdvancedConfig,
  DailyCallOptions,
  DailyQualityTestResult,
} from '@daily-co/daily-js';
import { DailyProvider, useDaily } from '@daily-co/daily-react';
import bowser from 'bowser';
import getConfig from 'next/config';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import useEventBridge from '/hooks/useEventBridge';
import { useShowLeaveButton } from '/lib/state/custom';

import { CallConfigProvider } from './CallConfigProvider';
import { CallMode, CallState, useCallMachine } from './useCallMachine';

interface Props {
  customHost?: string;
  domain: string;
  room: string;
  token?: string;
  isEmbedded: boolean;
  bypassRegionDetection: boolean;
  v2CamAndMic: boolean | number | null;
  useLegacyVideoProcessor: boolean;
  disableRecordingIndicator: boolean | null;
  roomsCheckOrigin?: string;
  apiHost?: string;
  micAudioMode?: DailyAdvancedConfig['micAudioMode'];
}

interface ContextValue {
  disableRecordingIndicator: boolean;
  disableAudio: boolean;
  enableCallButtons: boolean;
  initializeCallArgs(options: DailyCallOptions);
  leaveCall(): void;
  mode: CallMode;
  preJoinNonAuthorized: boolean;
  reloadCall(): void;
  setRedirectOnLeaveCall(redirect: boolean): void;
  state: CallState;
}

export const CallContext = createContext<ContextValue>(null);

export const CallProvider: React.FC<React.PropsWithChildren<Props>> = ({
  apiHost = null,
  bypassRegionDetection = false,
  children,
  customHost,
  disableRecordingIndicator = false,
  domain,
  isEmbedded,
  micAudioMode = 'speech',
  room,
  roomsCheckOrigin = null,
  token = '',
  useLegacyVideoProcessor,
  v2CamAndMic,
}) => {
  const [preJoinNonAuthorized, setPreJoinNonAuthorized] = useState(false);

  const {
    daily,
    disableAudio,
    initializeCallArgs,
    leave,
    mode,
    setRedirectOnLeave,
    state,
  } = useCallMachine({
    domain,
    room,
    token,
    customHost,
    isEmbedded,
    bypassRegionDetection,
    v2CamAndMic,
    useLegacyVideoProcessor,
    roomsCheckOrigin,
    apiHost,
    micAudioMode,
  });

  /**
   * Only enable the call buttons (camera toggle, leave call, etc.) if we're joined
   * or if we've errored out.
   *
   * !!!
   * IMPORTANT: calling callObject.destroy() *before* we get the "joined-meeting"
   * can result in unexpected behavior. Disabling the leave call button
   * until then avoids this scenario.
   * !!!
   */
  const enableCallButtons = ['joined', 'error'].includes(state);

  const reloadCall = useCallback(() => {
    const url = new URL(window.location.href);
    if (!!token) {
      url.searchParams.set('t', token);
    }
    location.href = url.toString();
  }, [token]);

  useEffect(() => {
    if (!daily) return;
    const { access } = daily.accessState();
    if (access === 'unknown') return;
    const requiresPermission = access?.level === 'lobby';
    setPreJoinNonAuthorized(requiresPermission && !token);
  }, [state, daily, token]);

  const [, setShowLeaveButton] = useShowLeaveButton();
  useEffect(() => {
    setShowLeaveButton(mode === 'direct-link');
  }, [mode, setShowLeaveButton]);

  const handleTestResult = (result: DailyQualityTestResult) => {
    switch (result) {
      case 'good':
      case 'warning':
        return;
      case 'bad':
        daily.updateSendSettings({
          video: 'bandwidth-optimized',
        });
        return;
    }
  };

  return (
    <CallContext.Provider
      value={{
        disableAudio,
        disableRecordingIndicator,
        enableCallButtons,
        initializeCallArgs,
        leaveCall: leave,
        mode,
        preJoinNonAuthorized,
        reloadCall,
        setRedirectOnLeaveCall: setRedirectOnLeave,
        state,
      }}
    >
      <DailyProvider callObject={daily} recoilRootProps={{ override: false }}>
        <CallConfigProvider>
          {children}
          <TestFrame state={state} onTestResult={handleTestResult} />
        </CallConfigProvider>
      </DailyProvider>
    </CallContext.Provider>
  );
};

export const useCallState = () => useContext(CallContext);

interface TestFrameProps {
  onTestResult(result: DailyQualityTestResult): void;
  state: CallState;
}

const TestFrame: React.FC<TestFrameProps> = ({ onTestResult, state }) => {
  const daily = useDaily();
  const { assetPrefix } = getConfig().publicRuntimeConfig;
  const [renderFrame, setRenderFrame] = useState(false);
  const testFrame = useRef<HTMLIFrameElement>(null);
  const testChannel = useRef<MessageChannel>(null);

  useEffect(() => {
    if (state === 'lobby') setRenderFrame(true);
  }, [state]);

  const handleTestIframeLoad = () => {
    testChannel.current = new MessageChannel();
    testChannel.current.port1.onmessage = (msg) => {
      if (msg.data?.results) {
        // @ts-ignore
        daily?._logCallQualityTestResults?.(msg.data.results);
        onTestResult(msg.data?.results?.result);
        setRenderFrame(false);
      }
    };
    const { browser, os } = bowser.parse(navigator.userAgent);
    const isAndroidChrome =
      browser.name.includes('Chrome') && os.name.includes('Android');
    const forceCanvas = isAndroidChrome;

    // Small timeout to allow iframe React code to initialize
    setTimeout(() => {
      testFrame.current.contentWindow.postMessage(
        {
          action: 'connect',
          forceCanvas,
        },
        '*',
        [testChannel.current.port2]
      );
    }, 100);
  };

  useEventBridge(
    'core-join-meeting',
    useCallback(() => {
      if (!testFrame.current) return;
      testFrame.current.contentWindow.postMessage(
        {
          action: 'stop',
        },
        '*'
      );
    }, [])
  );

  if (!renderFrame) return null;

  const runsInNextExport =
    process.env.NODE_ENV !== 'development' && window.__NEXT_DATA__.nextExport;
  const iframeFile = `test-quality-iframe${runsInNextExport ? '.html' : ''}`;

  return (
    <iframe
      ref={testFrame}
      onLoad={handleTestIframeLoad}
      allow="camera; microphone; autoplay"
      src={`${assetPrefix}/${iframeFile}?callObjectBundleUrlOverride=${daily.properties.dailyConfig.callObjectBundleUrlOverride}`}
      hidden
    ></iframe>
  );
};
